/**
 * Map load timeout for public prospect PropertyMap.
 * Cross-origin iframes cannot prove tile render success via onLoad alone.
 */
export const PUBLIC_PROSPECT_MAP_LOAD_TIMEOUT_MS = 10000;

export function createPublicProspectMapLoadSession(sessionId = 0) {
  return {
    sessionId,
    status: 'loading',
    remountKey: sessionId,
  };
}

/**
 * Whether an iframe event may mutate the active map session / clear its timer.
 * Stale events from a previous remount must never affect the current session.
 */
export function ownsPublicProspectMapIframeEvent(currentSessionId, eventSessionId) {
  return currentSessionId === eventSessionId;
}

/**
 * Skip the initial-mount URL effect so Safari does not destroy/recreate the first iframe.
 * Only a real previous→next URL change should reset the session.
 */
export function shouldResetMapSessionForEmbedUrlChange(previousEmbedUrl, nextEmbedUrl) {
  if (previousEmbedUrl === undefined) return false;
  return previousEmbedUrl !== nextEmbedUrl;
}

/** Pure reducer for map session transitions used by UI and tests. */
export function reducePublicProspectMapLoadSession(session, action) {
  const current = session || createPublicProspectMapLoadSession(0);
  switch (action?.type) {
    case 'property_change':
      return createPublicProspectMapLoadSession((current.sessionId || 0) + 1);
    case 'iframe_load':
      if (action.sessionId !== current.sessionId) return current;
      // Late onLoad after timeout may still complete the same iframe session.
      if (current.status !== 'loading' && current.status !== 'timed_out') return current;
      return { ...current, status: 'loaded' };
    case 'timeout':
      if (action.sessionId !== current.sessionId) return current;
      if (current.status !== 'loading') return current;
      return { ...current, status: 'timed_out' };
    case 'iframe_error':
      if (action.sessionId !== current.sessionId) return current;
      if (current.status === 'loaded') return current;
      return { ...current, status: 'errored' };
    case 'retry':
      return createPublicProspectMapLoadSession((current.sessionId || 0) + 1);
    default:
      return current;
  }
}
